import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import type { DetailedHTMLProps, InputHTMLAttributes } from 'react';
import { useRef, useState } from 'react';
import { useCallback, useEffect } from 'react';
import { navigate } from 'vike/client/router';

import type { Optional } from '@/renderer/types';
import {
  type AlertId,
  type IRasAlertAddData,
  dispatchRemoveAlert,
  eventTarget,
} from '@/renderer/utils/alerts';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { useIdleContext } from '@/renderer/utils/useIdle';

import { twMerge } from 'tailwind-merge';
import { Logo } from '../Logo';
import Avatar from './Avatar';

export default Alert;

export { Alerts };

// const DEFAULT_DURATION = 10000000;
const DEFAULT_DURATION = 8000;
const MAX_ALERTS = 5;

function Alert<
  P extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'id'> &
    Omit<Optional<IRasAlertAddData, 'id'>, 'message'>,
>({
  actions,
  // icon,
  children,
  id,
  status,
  uFirst,
  uLast,
  uId,
  uRole,
  // avatarSrc,
  href,
  permanent,
}: P) {
  const statusColor = status || 'info';
  const bg =
    statusColor === 'error'
      ? 'bg-[color-mix(in_srgb,var(--light-destructive)_5%,var(--color-white))] dark:bg-[color-mix(in_srgb,var(--dark-destructive)_5%,var(--color-zinc-900))]'
      : statusColor === 'warning'
        ? 'bg-[color-mix(in_srgb,var(--light-warning)_5%,var(--color-white))] dark:bg-[color-mix(in_srgb,var(--dark-warning)_5%,var(--color-zinc-900))]'
        : statusColor === 'success'
          ? 'bg-[color-mix(in_srgb,var(--light-success)_5%,var(--color-white))] dark:bg-[color-mix(in_srgb,var(--dark-success)_5%,var(--color-zinc-900))]'
          : '';

  const idle = useIdleContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies(href):
  // biome-ignore lint/correctness/useExhaustiveDependencies(id):
  useUpdateEffect(() => {
    if (!idle.inactive && location.pathname === href && id) {
      dispatchRemoveAlert(id);
    }
  }, [idle.inactive]);

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!href) {
        return;
      }

      if ((event.target as HTMLElement).matches('button, a, button *, a *')) {
        return; // don't handle href clicks if the user clicked a button or anchor inside the alert
      }

      navigate(href);
      if (id && !permanent) {
        dispatchRemoveAlert(id);
      }
    },
    [href, id, permanent],
  );

  const showAvatar = !!uFirst && !!uLast && !!uId;
  const showLogo = !showAvatar;

  return (
    <>
      <div
        className="flex w-full flex-col items-center space-y-4 sm:items-end sm:pl-10"
        onClick={onClick}
      >
        <div
          className={twMerge(
            'pointer-events-auto flex w-full max-w-sm divide-x divide-zinc-200 rounded-sm border border-zinc-900/5 bg-white shadow-lg dark:divide-zinc-700 dark:border-white/10 dark:bg-zinc-900 dark:shadow-none',
            bg,
          )}
        >
          <div className="w-0 flex-1 p-4">
            <div className="flex items-start">
              <div className="shrink-0 pt-0.5">
                {showAvatar && (
                  <Avatar
                    user={{ firstName: uFirst, lastName: uLast, id: uId }}
                    isAdmin={uRole === 'admin' || uRole === 'staff'}
                  />
                )}
                {showLogo && <Logo className="h-10 w-10 select-none rounded-sm" />}
              </div>
              <div className="ml-3 w-0 flex-1 pt-0.5">{children}</div>
              {!!id && !permanent && !actions?.length && (
                <div className="ml-4 flex shrink-0">
                  <button
                    type="button"
                    className="inline-flex rounded-md bg-white text-zinc-400 hover:text-zinc-500 focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:bg-zinc-900"
                    onClick={() => dispatchRemoveAlert(id)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex max-w-[33%] shrink whitespace-break-spaces text-wrap">
            <div className="flex flex-col divide-y divide-zinc-200 dark:divide-zinc-700">
              <div className="flex h-0 flex-1">
                {actions?.map((action, i) => {
                  const showDismiss = !!id && !permanent;
                  const Component = action.href ? ('a' as const) : ('button' as const);

                  return (
                    <Component
                      key={action.href || i}
                      href={action.href}
                      type="button"
                      className={clsx(
                        'flex w-full items-center justify-center rounded-none border border-transparent px-3 py-2 text-center font-medium text-emerald-500 text-sm hover:text-emerald-400 focus:z-10 focus:outline-hidden focus:ring-2 focus:ring-emerald-500',
                        i === 0 && 'rounded-tr-lg',
                        !showDismiss && i === actions.length - 1 && 'rounded-br-lg',
                      )}
                      onClick={action.onClick}
                      // onClick={() => {
                      //   setShow(false);
                      // }}
                    >
                      {action.text}
                    </Component>
                  );
                })}
              </div>
              {!!id && !permanent && actions?.length && (
                <div className="flex h-0 flex-1">
                  <button
                    type="button"
                    className="flex w-full items-center justify-center rounded-none rounded-br-lg border border-transparent px-3 py-2 font-medium text-sm text-zinc-700 hover:text-zinc-500 focus:outline-hidden focus:ring-2 focus:ring-emerald-500"
                    onClick={() => dispatchRemoveAlert(id)}
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Alerts() {
  const ids = useRef<Set<AlertId>>(new Set());
  const [alerts, setAlerts] = useState<{ [i: AlertId]: IRasAlertAddData }>({});
  // const alerts = AlertsStore.useState(s => s.alerts);

  const addCallback = useCallback((event: CustomEvent<IRasAlertAddData>) => {
    const {
      actions,
      icon,
      id,
      message,
      status,
      submessage,
      pre,
      duration,
      href,
      permanent,
      uFirst,
      uLast,
      uId,
      uRole,
    } = event.detail;

    setAlerts((oldAlerts) => {
      let _timeout;

      if (oldAlerts[id]) {
        clearTimeout(oldAlerts[id]._timeout);
        ids.current.delete(id); // we will re-add it at the end of the stack
      }

      ids.current.add(id);

      if (!IS_SERVER && (duration === undefined || duration) && !permanent) {
        _timeout = setTimeout(() => {
          dispatchRemoveAlert(id);
        }, duration || DEFAULT_DURATION);
      }

      return {
        ...oldAlerts,
        [id]: {
          actions,
          icon,
          id,
          message,
          status,
          submessage,
          pre,
          duration,
          href,
          permanent,
          uFirst,
          uLast,
          uId,
          uRole,
          _timeout,
        },
      };
    });
  }, []);

  const removeCallback = useCallback((event: CustomEvent<AlertId>) => {
    const id = event.detail;

    if (ids.current.has(id)) {
      setAlerts((oldAlerts) => {
        if (oldAlerts[id]) {
          const { [id]: old, ...remainingAlerts } = oldAlerts;

          clearTimeout(old._timeout);

          ids.current.delete(id);

          return remainingAlerts;
        }

        return oldAlerts;
      });
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    // Only happens client-side
    eventTarget.addEventListener('rasalertadd', addCallback, false);
    eventTarget.addEventListener('rasalertremove', removeCallback, false);

    return () => {
      eventTarget.removeEventListener('rasalertadd', addCallback, false);
      eventTarget.removeEventListener('rasalertremove', removeCallback, false);
    };
  }, []);

  const renderIds = [...ids.current]
    .slice(Math.max(ids.current.size - MAX_ALERTS, 0), Math.max(ids.current.size - 1, MAX_ALERTS))
    .reverse();

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-50 flex flex-col items-end space-y-2 px-4 py-6 sm:items-start sm:p-6"
    >
      {/* <div className="fixed right-1 top-1 z-50 flex max-w-(--breakpoint-sm) flex-col space-y-2"> */}
      {renderIds.map((key) => {
        const alert = alerts[key];

        return (
          !!alert && (
            <Alert
              id={alert.id}
              key={alert.id}
              status={alert.status}
              icon={alert.icon}
              actions={alert.actions}
              href={alert.href}
              permanent={alert.permanent}
              uFirst={alert.uFirst}
              uLast={alert.uLast}
              uId={alert.uId}
              uRole={alert.uRole}
            >
              <div>
                {!!alert.submessage && (
                  <div className="line-clamp-4 break-words font-medium text-sm text-zinc-900 dark:text-zinc-500">
                    {alert.submessage}
                  </div>
                )}
                {!!alert.pre && (
                  <code className="line-clamp-4 break-all font-normal text-xs text-zinc-500 dark:text-white">
                    {alert.pre}
                  </code>
                )}
                <div className="mt-1 line-clamp-4 break-words text-sm text-zinc-500 dark:text-white">
                  {alert.message}
                </div>
              </div>
            </Alert>
          )
        );
      })}
    </div>
  );
}
